import { imagePath as localImagePath, assetPath as localAssetPath } from "./offlineStore";

// URL d'illustration : TCGdex sert des URL SANS extension auxquelles il faut
// ajouter le format voulu (`.../logo` -> `.../logo.webp`), alors que les replis
// tierces (Pokécardex) sont déjà des fichiers complets (`.../PBL.png`).
// Ajouter `.webp` à ces derniers casserait l'image, d'où ce garde-fou unique
// plutôt qu'un `.webp` recopié dans chaque composant.
const HAS_EXTENSION = /\.(png|jpe?g|webp|gif|svg)$/i;

/** Logo ou symbole de set, prêt à être mis dans un <img src>. */
export const setAsset = (url) => {
  if (!url) return url;
  return HAS_EXTENSION.test(url) ? url : `${url}.webp`;
};

/**
 * onError d'un <img src={setAsset(...)}>. Certains sets TCGdex (ex. `me05`
 * Nuit Noire, constaté le 2026-08-06) ne servent leur logo/symbole qu'en
 * `.png` : le `.webp` ajouté par `setAsset` y renvoie un 404 alors qu'il
 * marche pour la quasi-totalité des autres sets. Plutôt que de deviner quels
 * sets sont concernés, on retente une fois en `.png` sur tout échec de
 * `.webp`, et on masque seulement si `.png` échoue aussi (repli Pokécardex
 * déjà en `.png`/`.jpg` complet : `HAS_EXTENSION` l'exclut de `setAsset`,
 * jamais concerné par ce repli).
 */
export const handleSetAssetError = (e) => {
  const img = e.currentTarget;
  // src local (cache hors ligne : data: ou fichier natif) : rien à retenter,
  // la copie locale a échoué à charger, on masque juste.
  if (!img.src.startsWith("http")) { img.style.display = "none"; return; }
  if (img.src.endsWith(".webp") && !img.dataset.pngRetried) {
    img.dataset.pngRetried = "1";
    img.src = img.src.slice(0, -".webp".length) + ".png";
  } else {
    img.style.display = "none";
  }
};

// --- Images de carte : URL réseau + chemin du cache local hors ligne ---
// (étape 2 du mode hors ligne, cf. offlineStore.js pour le stockage et
// hooks/useCardImage.js pour la lecture locale-d'abord)

/** URL réseau d'une image de carte — le même calcul que chaque composant
 * répétait avant (`card.image_full ? card.image : ...`), centralisé. */
export const remoteCardImageSrc = (card, quality = "low") =>
  card.image_full ? card.image : `${card.image}/${quality}.webp`;

// Extension du fichier réellement servi : celle de l'URL complète quand
// `image_full` (Pokécardex/EN), sinon toujours .webp (repli TCGdex).
export const urlExt = (url) => {
  const m = /\.[a-z0-9]+$/i.exec(new URL(url, "http://x").pathname);
  return m ? m[0].toLowerCase() : ".webp";
};

/** Set d'appartenance d'une carte, pour retrouver son dossier local. Une
 * carte enrichie porte `card.set.id` ; en repli (carte non enrichie), même
 * règle que server.py : le préfixe de l'id avant le dernier "-". */
export const cardSetId = (card) =>
  card.set?.id || card.id.slice(0, card.id.lastIndexOf("-"));

/** Chemin local attendu pour cette carte/qualité. `image_full` n'a qu'un
 * seul fichier possible (Pokécardex sert déjà la résolution max) donc pas
 * de suffixe qualité dans ce cas — sinon la fiche en grand ("high") pointe
 * vers un fichier distinct des vignettes ("low"), qui n'est pas encore
 * synchronisé par cette étape (fast-follow HQ à la demande) mais dont le
 * chemin est déjà prêt pour quand ce sera le cas. */
export const cardLocalImagePath = (card, quality = "low") => {
  if (card.image_full) return localImagePath(cardSetId(card), card.id, urlExt(card.image));
  const suffix = quality === "high" ? "-hd" : "";
  return localImagePath(cardSetId(card), `${card.id}${suffix}`, ".webp");
};

/** Sprite d'une espèce par numéro national (même source statique que le
 * Pokédex : dépôt GitHub public PokeAPI, ~1 Ko, sans auth). Sert aussi à
 * l'avatar de la barre du haut. */
export const pokemonSpriteUrl = (dexId) =>
  `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${dexId}.png`;

// Empreinte courte d'une URL (djb2, base 36).
const urlKey = (url) => {
  let h = 5381;
  for (let i = 0; i < url.length; i++) h = ((h * 33) ^ url.charCodeAt(i)) >>> 0;
  return h.toString(36);
};

/** Chemin local du logo/symbole d'un set (cf. offlineStore.assetPath).
 * L'URL fait partie du nom : un fichier local ne peut ainsi servir que pour
 * l'URL même qui l'a produit (un logo qui change d'adresse est retéléchargé,
 * et une copie fausse enregistrée sous un autre nom n'est jamais reprise). */
export const setAssetLocalPath = (setId, kind, url) =>
  localAssetPath(setId, `${kind}-${urlKey(setAsset(url))}`, urlExt(setAsset(url)));
